/**
 * Торговые сессии: какая идёт и какая следующая.
 *
 * Крипта торгуется круглосуточно, но люди — нет. Объём приходит вместе
 * с рабочим днём Токио, Лондона и Нью-Йорка, и вопрос «почему рынок
 * стоит» чаще всего имеет ответ «потому что все спят».
 *
 * Считается здесь, а не на сервере: это чистая арифметика времени, ей
 * не нужны ни биржа, ни сеть. Заодно и часовой пояс получается тот, в
 * котором сидит человек, а не тот, где стоит сервер.
 *
 * **Главная сложность — переход на летнее время.** Лондон открывается в
 * восемь утра по местному, а по UTC это то семь, то восемь; Нью-Йорк
 * съезжает на час дважды в год, причём в другие даты, чем Европа.
 * Поэтому границы считаются через настоящие часовые зоны, а не
 * прибавлением смещения: зашитое «13:30 UTC» полгода показывало бы
 * неправду.
 *
 * **Праздники бирж** берутся из общего календаря (`@specter/rules`,
 * `holidays.ts`) — но только как пометка. Сессия — это часы региона, а
 * не одной биржи: в японский праздник Азия торгуется без Токио (Гонконг,
 * Сингапур, Сидней работают, крипта — круглосуточно), просто тоньше.
 * Выбрасывать такую сессию значило бы сказать неправду: движение в эти
 * часы есть. Поэтому праздник подписывается, а часы остаются.
 */

import { EXCHANGE_CITY, holidayOn, holidaysOf, type Exchange, type ExchangeHoliday } from '@specter/rules';

export interface SessionSpec {
  readonly id: 'asia' | 'london' | 'new-york';
  readonly name: string;
  /** Зона, в которой заданы часы работы. */
  readonly zone: string;
  /** Часы и минуты открытия и закрытия по местному времени. */
  readonly open: readonly [number, number];
  readonly close: readonly [number, number];
}

/**
 * Три сессии, которыми размечены сутки.
 *
 * Часы — биржевые, местные. Азия взята по Токио, а не по Гонконгу или
 * Сингапуру: из трёх это самая ранняя, и именно с неё начинается
 * движение суток.
 */
export const SESSIONS: readonly SessionSpec[] = [
  { id: 'asia', name: 'ASIA', zone: 'Asia/Tokyo', open: [9, 0], close: [18, 0] },
  { id: 'london', name: 'LONDON', zone: 'Europe/London', open: [8, 0], close: [17, 0] },
  { id: 'new-york', name: 'NEW YORK', zone: 'America/New_York', open: [9, 30], close: [16, 0] },
];

/** Биржа, по календарю которой живёт сессия. */
export const EXCHANGE_OF: Readonly<Record<SessionSpec['id'], Exchange>> = {
  asia: 'tokyo',
  london: 'london',
  'new-york': 'new-york',
};

/** Праздник или сокращённый день у биржи сессии в эту местную дату. */
function holidayFor(session: SessionSpec, date: { y: number; m: number; d: number }): ExchangeHoliday | null {
  return holidayOn(EXCHANGE_OF[session.id], date.y, date.m, date.d);
}

/**
 * Открытие и закрытие сессии в местную дату — или `null` в выходной.
 *
 * Праздник биржи сессию не отменяет — он приходит пометкой: регион
 * торгует и без одной биржи, только тише.
 */
function spanOn(
  session: SessionSpec,
  date: { y: number; m: number; d: number },
): { open: number; close: number; holiday: ExchangeHoliday | null } | null {
  const open = instantOf(session.zone, date, session.open);
  if (!weekday(session.zone, open)) return null;
  const close = instantOf(session.zone, date, session.close);
  return { open, close: close > open ? close : close + DAY_MS, holiday: holidayFor(session, date) };
}

/** Открытие или закрытие сессии как момент времени. */
export interface SessionEdge {
  readonly session: SessionSpec;
  readonly at: number;
}

export interface SessionState {
  /** Идущая сейчас; `null` — все закрыты. */
  readonly current: SessionEdge | null;
  /** Когда закончится идущая. */
  readonly endsAt: number | null;
  /** Ближайшая из тех, что откроются. */
  readonly next: SessionEdge;
}

const DAY_MS = 86_400_000;

/**
 * Смещение зоны в этот момент, в миллисекундах.
 *
 * Через `Intl`, а не через таблицу правил: правила переходов меняют
 * страны, а не мы, и держать их копию у себя значит однажды отстать.
 */
function offsetOf(zone: string, at: number): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(at));

  const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? '0');
  // `24` вместо `00` попадается у полуночи в некоторых движках.
  const hour = get('hour') % 24;

  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), hour, get('minute'), get('second'));
  return asUtc - at;
}

/**
 * Местное «вчера/сегодня/завтра» как календарная дата в этой зоне.
 *
 * Нужна именно дата в зоне сессии: в Токио уже среда, когда в
 * Нью-Йорке ещё вторник, и «сегодняшнее открытие» у них разное.
 */
function dateIn(zone: string, at: number): { y: number; m: number; d: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(at));
  const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? '0');
  return { y: get('year'), m: get('month'), d: get('day') };
}

/**
 * Момент времени по местным часам в этой зоне.
 *
 * Смещение зависит от самого момента — в этом и подвох, — поэтому
 * считается в два шага: сначала грубо, потом по уточнённому смещению.
 *
 * Второй шаг здесь подстраховка, а не необходимость, и честнее сказать
 * это прямо. Он был бы нужен, если бы открытие сессии попадало на сами
 * часы перехода; у всех трёх зон переход приходится на воскресенье, а
 * выходные сессии пропускают, — то есть случай недостижим и проверкой
 * не покрыт. Оставлен потому, что стоит одного вызова, а список сессий
 * когда-нибудь пополнится зоной с другими правилами.
 */
function instantOf(
  zone: string,
  date: { y: number; m: number; d: number },
  hm: readonly [number, number],
): number {
  const wall = Date.UTC(date.y, date.m - 1, date.d, hm[0], hm[1], 0);
  const rough = wall - offsetOf(zone, wall);
  return wall - offsetOf(zone, rough);
}

/** Рабочий ли это день в зоне сессии: суббота и воскресенье не в счёт. */
function weekday(zone: string, at: number): boolean {
  const day = new Intl.DateTimeFormat('en-US', { timeZone: zone, weekday: 'short' }).format(
    new Date(at),
  );
  return day !== 'Sat' && day !== 'Sun';
}

/**
 * Открытия сессии на несколько суток вокруг момента.
 *
 * Смотрим и назад, и вперёд: сессия, открывшаяся вчера по местному
 * времени, может идти прямо сейчас — Нью-Йорк закрывается уже за
 * полночь по Токио.
 */
function edgesAround(
  session: SessionSpec,
  now: number,
): { readonly open: number; readonly close: number }[] {
  const out: { open: number; close: number }[] = [];
  const seen = new Set<number>();

  // Вперёд — на неделю: японская «золотая неделя» вместе с выходными
  // держит Токио закрытым пять дней подряд, и следующее открытие дальше
  // двух суток.
  for (let shift = -2; shift <= 7; shift += 1) {
    const span = spanOn(session, dateIn(session.zone, now + shift * DAY_MS));
    if (!span || seen.has(span.open)) continue;
    seen.add(span.open);
    out.push({ open: span.open, close: span.close });
  }

  return out.sort((a, b) => a.open - b.open);
}

/** Одна сессия одного рабочего дня: когда открылась и когда закрылась. */
export interface SessionSpan {
  readonly session: SessionSpec;
  readonly open: number;
  readonly close: number;
  /** Праздник или сокращённый день у биржи сессии — пометка, не отмена. */
  readonly holiday: ExchangeHoliday | null;
}

/**
 * Все сессии, открывшиеся в промежутке, — по рабочим дням.
 *
 * Для графика: индикатор рисует ход каждой сессии, и ему нужны ровно
 * те же открытия и закрытия, что показывает «Главная», — с летним
 * временем и без выходных. Своя таблица часов у индикатора уже
 * разошлась с «Главной»: Азию он закрывал на час раньше, а субботу и
 * воскресенье размечал как обычные дни.
 */
export function sessionsBetween(from: number, to: number): SessionSpan[] {
  const out: SessionSpan[] = [];
  for (const session of SESSIONS) {
    const seen = new Set<number>();
    // С запасом в сутки с обеих сторон: местная дата в зоне сессии может
    // отличаться от даты по UTC.
    for (let at = from - DAY_MS; at <= to + DAY_MS; at += DAY_MS) {
      const span = spanOn(session, dateIn(session.zone, at));
      if (!span || seen.has(span.open) || span.open < from || span.open > to) continue;
      seen.add(span.open);
      out.push({ session, ...span });
    }
  }
  return out.sort((a, b) => a.open - b.open);
}

/**
 * Что идёт сейчас и что будет следующим.
 *
 * Одновременно открытых бывает две — Лондон и Нью-Йорк заметно
 * перекрываются, и это самые живые часы суток. Показывается та, что
 * открылась позже: именно она задаёт тон, а перекрытие видно по
 * полоске.
 */
export function sessionState(now: number): SessionState {
  let current: SessionEdge | null = null;
  let endsAt: number | null = null;
  let next: SessionEdge | null = null;

  for (const session of SESSIONS) {
    for (const edge of edgesAround(session, now)) {
      if (edge.open <= now && now < edge.close) {
        if (!current || edge.open > current.at) {
          current = { session, at: edge.open };
          endsAt = edge.close;
        }
      } else if (edge.open > now && (!next || edge.open < next.at)) {
        next = { session, at: edge.open };
      }
    }
  }

  /*
   * Следующая есть всегда: окно в пять суток покрывает и выходные,
   * когда до понедельника ждать дольше обычного. Но подстраховка нужна
   * — пустое поле здесь уронило бы главную, а не просто показало бы
   * меньше.
   */
  if (!next) {
    const fallback = SESSIONS[0]!;
    next = { session: fallback, at: now + DAY_MS };
  }

  return { current, endsAt, next };
}

/**
 * Открытие и закрытие сессии в те сутки, на которые пришёлся момент.
 *
 * Сутки берутся местные — в Токио уже среда, когда в Нью-Йорке ещё
 * вторник, и «сегодняшнее открытие» у них разное.
 */
export function sessionDay(
  session: SessionSpec,
  now: number,
): { readonly open: number; readonly close: number } {
  const date = dateIn(session.zone, now);
  return {
    open: instantOf(session.zone, date, session.open),
    close: instantOf(session.zone, date, session.close),
  };
}

/** Праздник сессии: какой, когда и что это значит для торгов. */
export interface SessionHoliday {
  readonly session: SessionSpec;
  readonly holiday: ExchangeHoliday;
  /** Город биржи словами: «в Токио». */
  readonly where: string;
  /** Полночь местной даты праздника — для «сегодня» и «в пятницу». */
  readonly at: number;
  /** Сегодня ли по местному времени биржи. */
  readonly today: boolean;
  /** Через сколько дней: 0 — сегодня, 1 — завтра. */
  readonly inDays: number;
  /** Во сколько закроется в сокращённый день — по часам смотрящего. */
  readonly closesAt: number | null;
}

/**
 * Праздники бирж на ближайшие дни, начиная с сегодняшнего.
 *
 * «Сегодня» — по местной дате биржи: в Токио уже праздник, когда у
 * смотрящего в Москве ещё вечер накануне.
 */
export function holidaysAhead(now: number, days = 3): SessionHoliday[] {
  const out: SessionHoliday[] = [];
  for (const session of SESSIONS) {
    const exchange = EXCHANGE_OF[session.id];
    const seen = new Set<string>();
    for (let shift = 0; shift <= days; shift += 1) {
      const date = dateIn(session.zone, now + shift * DAY_MS);
      const id = `${date.y}-${date.m}-${date.d}`;
      if (seen.has(id)) continue;
      seen.add(id);
      const holiday = holidayFor(session, date);
      if (!holiday) continue;
      const midnight = instantOf(session.zone, date, [0, 0]);
      out.push({
        session,
        holiday,
        where: EXCHANGE_CITY[exchange].where,
        at: midnight,
        today: shift === 0,
        inDays: shift,
        closesAt: holiday.close ? instantOf(session.zone, date, holiday.close) : null,
      });
    }
  }
  return out.sort((a, b) => a.at - b.at);
}

/**
 * Ближайший праздник любой из трёх бирж — после сегодняшнего дня.
 *
 * Нужен «Главной», когда в ближайшие дни праздников нет: иначе блок о
 * праздниках просто исчезал, и казалось, что календаря нет вовсе.
 * Считается по годовым календарям, без перебора дней: дёшево даже раз в
 * минуту.
 */
export function nextHoliday(now: number): SessionHoliday | null {
  let best: SessionHoliday | null = null;
  for (const session of SESSIONS) {
    const exchange = EXCHANGE_OF[session.id];
    const today = dateIn(session.zone, now);
    const todayKey = `${today.y}-${String(today.m).padStart(2, '0')}-${String(today.d).padStart(2, '0')}`;
    const todayMidnight = instantOf(session.zone, today, [0, 0]);
    for (const year of [today.y, today.y + 1]) {
      for (const holiday of holidaysOf(exchange, year).values()) {
        if (holiday.date <= todayKey) continue;
        const [y, m, d] = holiday.date.split('-').map(Number) as [number, number, number];
        const at = instantOf(session.zone, { y, m, d }, [0, 0]);
        if (best && best.at <= at) continue;
        best = {
          session,
          holiday,
          where: EXCHANGE_CITY[exchange].where,
          at,
          today: false,
          inDays: Math.round((at - todayMidnight) / DAY_MS),
          closesAt: holiday.close ? instantOf(session.zone, { y, m, d }, holiday.close) : null,
        };
      }
      // Нашёлся в этом году — в следующем искать незачем.
      if (best && best.session === session) break;
    }
  }
  return best;
}

/**
 * Часы работы сессии в часовом поясе смотрящего.
 *
 * Показывать биржевое местное время значило бы заставить человека
 * пересчитывать его в уме — а это ровно та работа, ради избавления от
 * которой виджет и ставят.
 */
export function localHours(session: SessionSpec, now: number): string {
  const { open, close } = sessionDay(session, now);
  return `${clock(open)} — ${clock(close)}`;
}

function clock(at: number): string {
  return new Date(at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Сколько осталось словами: «4ч 30м».
 *
 * Секунды показываются только на последнем часе: раньше они лишь
 * мельтешат, а на исходе — единственное, что важно.
 */
export function untilWords(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  if (hours > 0) return `${hours}ч ${minutes}м`;
  if (minutes > 0) return `${minutes}м ${String(seconds).padStart(2, '0')}с`;
  return `${seconds}с`;
}

/** Доля пройденного от открытия до закрытия, 0..1. */
export function progressOf(from: number, to: number, now: number): number {
  if (to <= from) return 0;
  return Math.min(1, Math.max(0, (now - from) / (to - from)));
}
